/*
    Implements an interface by which client code can send logs to an ElasticLog endpoint.
*/

import { getAudioPrimed, setAudioPrimedHook, getUseFirestore} from '../constants/Meta';
import { ajax } from './Network.js';
import { throttledConsoleLog, getShared, makeId, allowMulti, throttleAllow, isMobile, clearFetch } from './Utils.js';
import { getConfig, getLoadQueryParams } from '../utils/configUtils';


import sleep from 'sleep-promise';

let asyncErr;
let sendLoopHeartbeat = Date.now();
let sendLoopId = 1;

const mobile = isMobile(true);

let ip = undefined;
let ipWhen = 0;
let cachedIpJson = undefined;
let ipCallUnderway = false;

let elasticLogInited = false;
let svcsUp = true;

let maxSendDelayTicks = 3000;
let minNetTicks = 5000;

const elasticLogConfig = (data, throttleKey) => elasticLog({ type: ElasticTypes.config, data, throttleKey });
const elasticLogTaskAction = (data, throttleKey) => elasticLog({ type: ElasticTypes.taskaction, data, throttleKey });
const elasticLogMessage = (data, throttleKey) => elasticLog({ type: ElasticTypes.message, data, throttleKey });
const elasticLogNetwork = (data, throttleKey) => elasticLog({ type: ElasticTypes.network, data, throttleKey });
const elasticLogException = (data, throttleKey) => elasticLog({ type: ElasticTypes.exception, data, throttleKey });
const elasticLogEvent = (data, throttleKey) => elasticLog({ type: ElasticTypes.event, data, throttleKey });
const elasticLogError = (data, throttleKey) => elasticLog({ type: ElasticTypes.error, data, throttleKey });

const shortErrMsgs = {
  'Failed to get document because the client is offline.': 'Offline*',
  'There is no user record corresponding to this identifier. The user may have been deleted.': 'User not found*',
  'A network error (such as timeout, interrupted connection or unreachable host) has occurred.': 'Network disruption*',
};

//elasticTypeNames must be lower-case (a constraint imposed by elasticSearch)
const elasticTypeNames = ['config', 'exception', 'taskaction', 'message', 'network', 'event', 'heartbeat', 'error'];
const ElasticTypes = elasticTypeNames.reduce((accum, cur) => {
  if (cur.toLowerCase() != cur) throw new Error('Elastic type names must be lower case: ' + cur);
  accum[cur] = cur;
  return accum;
}, {});
let unitConfig = {};

let session = makeId(4);

let ordinal = -1; //pre-incremented when used, so first value will be zero
let nextNonNetOrdinal = -1;

let catalogErrorFunc;
let recordQueue = [];
const MAX_BACKED_UP_RECORDS = 500; //max number of unsent records we'll accumulate; arbitrary

let machineId;

export function getMachineUid() {
  return (machineId || {}).id;
}

export function getUnitConfig() {
  return unitConfig;
}

function storeValue(key, stringValue) {
  localStorage.setItem(key, stringValue);
}

function removeValue(key) {
  localStorage.removeItem(key);
}

export function logSetSvcsUp(svcsUpParam) {
  svcsUp = svcsUpParam;
}

/*
 * Retrieves a string from the platform-specific persistent store used by storeValue.
 *
 * @param {string} key - the key used to previously store a string value.
 * @return {string} the value associated with the specified key, or undefined if none.
 */
function getValue(key) {
  return localStorage.getItem(key);
}

/*
  Establishes a machine-specific unique id intended to persist across launches of OnDemand.
  If the machine id already exists, this routine will re-write it, to aid in exploring whether 
  there is an age-based policy that can be avoided by so doing. [bart-1291]

  The following ios article for some context:
    https://webkit.org/blog/10218/full-third-party-cookie-blocking-and-more/
    (Note section "7-Day Cap on All Script-Writeable Storage")
 */
function setOrRefreshMachineId() {
  try {
    const DiligentRobots = 'DiligentRobots';

    if (!machineId) {
      let unparsed = getValue(DiligentRobots);
      if (unparsed) {
        try {
          machineId = JSON.parse(unparsed);
        }
        catch (err) {
          elasticLogException({ what: 'midParseErr', unparsed, msg: 'Error parsing machineId: ' + err });
        }
      }

      if (!machineId) {
        machineId = {
          created: new Date().toISOString(),
          id: makeId(4),
        };
        elasticLogEvent({ what: 'newMid', machineId, msg: `Created new mid ${machineId.id}` });
      }
    }

    //Store, regardless of whether it was there already.
    //Remove it first, in case age timestamping done from first insert
    //rather than most recent write.
    removeValue(DiligentRobots);
    storeValue(DiligentRobots, JSON.stringify(machineId));
  }
  catch (err) {
    console.log('setOrRefreshMachineId error: ' + err);
    elasticLogException({ what: 'setMidErr', msg: '' + err });
  }
}

function sendOneHeartbeat() {
  let data = {
    tzOffset: new Date().getTimezoneOffset(),
    agent: navigator.userAgent,
    mobile,
    audio: getAudioPrimed(),
    svcsUp,
    firestore: getUseFirestore()
  };

  if (ip) {
    data.ip = ip;
    data.ipWhen = ipWhen;
  }

  elasticLog({ type: ElasticTypes.heartbeat, data });
}

/*
 * Periodically posts a log indicating that moxi is running.
 */
function heartbeatLoop() {
  const start = Date.now();
  try {
    sendOneHeartbeat();
    setOrRefreshMachineId();
    cacheIpAsync();
  }
  catch (err) {
    elasticLogException({ what: 'heartErr', msg: '' + err });
  }

  let nextTicks = Math.max(0, 1000 * 60 * 3 - (Date.now() - start)); //every 3 minutes, drift-free
  setTimeout(heartbeatLoop, nextTicks);
}

/*
 * Does a best-effort latch retrieval of the function used to catalog errors, and returns
 * either that function or a no-op function that can be safely called.
 */
function getCatalogErrFunc() {
  catalogErrorFunc = catalogErrorFunc || getShared()['catalogError'];
  return catalogErrorFunc || (() => {});
}

async function cacheIpAsync() {
  await getIP(({ json, when }) => {
    if (json && json.ip && !json.error) {
      if (ip && ip != json.ip)
        elasticLogEvent({
          what: 'ipChange',
          oldIP: ip,
          newIP: json.ip,
          prevIpReadTicks: ipWhen,
          msg: `IP change ${ip} => ${json.ip}`,
        });

      ip = json.ip;
      ipWhen = when;
    }
  }, 1000);
}

/*
 * Initializes elasticLogging by retrieving/setting the logging configuration, and
 * by starting the heartbeat logs.
 *
 * @param firestore is a firestore client reference.
 * @param siteName is the name of the site in firestore.
 */
async function initElasticLog() {

  getShared().elasticLogError = elasticLogError;

  cacheIpAsync();

  if (elasticLogInited) return;
  elasticLogInited = true;

  setAudioPrimedHook(() => {
    sendOneHeartbeat();   //so we don't end up waiting for log clarity that audio is enabled
    elasticLogEvent({what:'audioPrimed', msg:`Audio is now primed`});
  }); 

  let shared = getShared();
  shared['elasticLogError'] = elasticLogError;
  getCatalogErrFunc();

  setOrRefreshMachineId();

  setTimeout(() => heartbeatLoop(), 1000 * 3); //slight delay to increase likely prior retrieval of ip address
}

function makePath(pathStr) {
  return pathStr.split('.');
}

//This is a collection of adhoc-discovered type error corrections
const scrubFilters = [
  { path: makePath('error'), isGood: (x) => typeof x == 'object' }, //elastic type: object
  { path: makePath('json.lastScanTimestamp'), isGood: (x) => !!x && typeof x == 'string' }, //elastic type: date (rejects empty strings)
];

function scrub(scrubbed, data, { path, isGood }) {
  if (!path.length) return scrubbed;

  let x = data,
    nextSeg,
    parent;
  for (let seg of path) {
    nextSeg = seg;
    parent = x;
    if (typeof x != 'object') return scrubbed; //absent (untraversable parent); no change
    x = x[seg];
    if (x === undefined) return scrubbed; //absent (ancestor or self not present); no change
  }
  if (isGood(x)) return scrubbed; //present and good; no change

  //needs change
  scrubbed = scrubbed || {};
  scrubbed[path.join('__')] = typeof x == 'string' ? x : JSON.stringify(x); //strictly string key to string value
  delete parent[nextSeg]; //remove offending incarnation from data
  return scrubbed;
}

/*
  Prevent log rejection on the elastic side per schema friction.
*/
function schemaScrub(data) {
  if (!data || typeof data != 'object') return data;
  let scrubbed;
  scrubFilters.forEach((scrubFilter) => (scrubbed = scrub(scrubbed, data, scrubFilter)));
  if (scrubbed) data.scrubbed = scrubbed;
  return data;
}

/*
 * This routine produces/logs records with the following fields:
 *   - timeCurrent is an ISOString denoting the time at which the log is queued for sending.
 *   - timeSent is an ISOString denoting the time at which the log is actually sent.
 *   - site is the name of the site from which the record originates.
 *   - session is a unique session id generated for this OnDemand launch.
 *   - ordinal is a zero-based serial integer denoting the record's order in the session.
 *      This enables analysis of a session data set that reveals records that didn't get
 *      sent.
 *   - nnOrdinal is a zero-based serial interger denoting record order but which is only
 *      incremented on logs that are NOT of type "network".  Because network logs vastly
 *      outnumber non-network logs, many fetches from the dbase will opt to not include
 *      network logs (for efficiency reasons), so nnOrdinal enables analysis of missing
 *      ordinals in such situations.
 *   - version is an object that includes the branch, git hash, and date of the running
 *      build.
 *   - platform indicates whether the client is web or ios.
 *   - unitConfig is an object with the configuration of this app (what operator type, which
 *      robot, etc).
 *   - type is one of the values of ElasticTypes.
 *   - data is whatever information has been sent to accompany the 'type' value.
 *   - machineId is an object holding a machine-unique id and the date of issue of the id.
 *      The machineId is not guaranteed to persist in perpetuity on a given machine, but is
 *      likely to at least persist through multiple launches of OnDemand.
 *   - retries: integer denoting number of retries before this log was sent; if absent, 0 is
 *      implicit.
 *
 * @param type is a string denoting what type of elastic log this is; must be one of
 *    ElasticTypes.
 * @param data is an object containing whatever information the client deems useful context
 *    to accompany the type parameter.
 */
function elasticLog({ type, data = {}, throttleKey }) {
  //this top-level catch is to guarantee that synchronous elasticLog call doesn't throw errors
  try {

    if(throttleKey && !throttleAllow(throttleKey))
      return;

    console.log(`elasticLog: ${type} ${(ip ? 'ip=' + ip + ' ' : '')}` + JSON.stringify({ data, unitConfig: JSON.stringify(unitConfig) }, false, 2));

    const timeCurrent = new Date().toISOString(); //get immediate/accurate time
    if (type == ElasticTypes.config) {
      unitConfig = { ...data, timeConfigured: timeCurrent }; //hold onto unitConfig for this and future logs
      data = { what: 'config' };
    }

    //automatically console-surface messages, exceptions, and errors
    if (type == ElasticTypes.message || type == ElasticTypes.exception || type == ElasticTypes.error)
      console.log(type + ': ' + JSON.stringify(data));

    (async () => {
      //this async catch is to prevent exceptions in this stretch from
      //causing a loop where elasticLog exceptions trigger yet more
      //calls to elasticLog.
      try {
        if (!ElasticTypes[type]) return throttledConsoleLog('elasticLog:badType', `${type} ${JSON.stringify(data)}`);

        //construct the record
        let record;
        ++ordinal;

        let nnOrdinal = type != ElasticTypes.network ? ++nextNonNetOrdinal : undefined;

        if (type == ElasticTypes.message || type == ElasticTypes.exception || type == ElasticTypes.error) {
          if (!allowMulti(type, 60, 60)) return throttledConsoleLog(`elasticLog:throttle:${type}`, JSON.stringify(data));
        }
        data = { ...data }; //make a copy of data

        // data = schemaScrub(data);

        if (!data.what) {
          data.what = 'empty';
        }
        else if (typeof data.what != 'string') {
          data.origWhat = data.what;
          data.what = `nonstring:${typeof data.what}`;
        }

        if (!data.msg) {
          data.msg = 'empty';
        }
        else if (typeof data.msg != 'string') {
          try {
            data.msg = JSON.stringify(data.msg);
          }
          catch (err) {
            data.msg = `unstringifiable:${typeof data.msg}`;
          }
        }

        let reformulated;
        try {
          reformulated = JSON.stringify({
            ...data,
            loadQueryParams: getLoadQueryParams(),
            what: undefined,
            msg: undefined,
          });
        }
        catch (err) {
          reformulated = JSON.stringify({ unstringifiable: true });
        }

        const site = getConfig().site.name;

        record = {
          session,
          ordinal,
          nnOrdinal,
          site,
          version: getConfig().version,
          platform: 'web',
          type,
          reformulated,
          data: { what: data.what, msg: data.msg },
          unitConfig,
          timeCurrent,
          "@timestamp": timeCurrent,
          machineId,
          ip,
        };
        record.minNetTicks = minNetTicks;

        //if we have "too many" unsent records, don't add this one
        if (recordQueue.length > MAX_BACKED_UP_RECORDS)
          return throttledConsoleLog('elasticLog:tooManyRecords', `${type} ${JSON.stringify(record)}`);

        //queue the record
        recordQueue.push(record); //queue for sending

        if (recordQueue.length > 1) {
          //in theory another process is already sending records and we don't need to...
          // but check here to see if there are signs of trouble before moving on.

          let loopHeartbeatGap = Date.now() - sendLoopHeartbeat;
          if (loopHeartbeatGap < 1000 * 30) return; //we choose to think that the async send thread might be working...

          //It's been long enough since the loop timestamp changed that something seems screwy
          while (recordQueue.length > MAX_BACKED_UP_RECORDS) recordQueue.pop();

          ++ordinal;
          let msg = `Restarting log-send, gap=${Math.floor(loopHeartbeatGap / 1000)}s`;
          if (asyncErr) msg += `, err="${asyncErr.errStr}"`;
          let data = { what: 'logRestart', msg, gap: loopHeartbeatGap, asyncErr };
          let record = {
            session,
            ordinal,
            nnOrdinal: ++nextNonNetOrdinal,
            site,
            version: getConfig().version,
            platform: 'web',
            type: ElasticTypes.message,
            data,
            unitConfig,
            timeCurrent,
            machineId,
          };
          recordQueue.unshift(record); //front of the line

          asyncErr = undefined; //clear the error
          ++sendLoopId; //abort the prev loop if it still exists
          //... and continue on to the loop below
        }

        //recordQueue just went from empty to non-empty, this process is now in charge of emptying the queue
        let sendDelayTicks = 0; //milliseconds between send attempts
        let sendLoopIdCopy = sendLoopId;
        while (recordQueue.length) {
          if (sendLoopIdCopy != sendLoopId) return; //this loop has been obviated by another

          sendLoopHeartbeat = Date.now();

          let recordToSend = recordQueue[0];
          recordToSend['timeSent'] = new Date().toISOString();

          // if Firestore net_config field fsCredsOverride = true, use Elastic creds from Firestore instead of the default from MBot backend
          let elasticCredsFromFirestore = getConfig().firestoreElastic;
          let netConfig = elasticCredsFromFirestore?.fsCredsOverride ? elasticCredsFromFirestore : getConfig().mbotElastic;

          // if there's no Elastic creds from backend yet, skip the attempt and go straight into retry timeout
          if (netConfig) {
            let options = {
              url: `${netConfig.url}/${netConfig.index}`,
              type: 'POST',
              contentType: 'application/json',
              dataType: 'json',
              data: JSON.stringify(recordToSend),
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
            };

            let authStr = netConfig.creds?.trim();
            if (authStr) {
              authStr = authStr.indexOf(' ') >= 0 ? authStr : `Basic ${authStr}`;
              options.headers['Authorization'] = authStr;
            }

            if (netConfig.apiKeyExp && netConfig.apiKeyExp <= Date.now()) {
              throttledConsoleLog('elasticLog:credsExpired', `Elastic creds expired: netConfig.apiKeyExp = ${netConfig.apiKeyExp}`);
            }

            let ajaxValue = await ajax(options);

            if (ajaxValue.error) {
              //failure, did not send
              throttledConsoleLog('elasticLog:ajaxErr', ajaxValue.error.msg);

              let status = ajaxValue.error.status || 0;
              if (status >= 400 && status < 500) {
                //elastic doesn't like this record for whatever finicky reasons.
                // Assume retries of the same will not get through.
                // Operate on the theory that it's something in the data record; reformulate and retry once.
                if (recordToSend.reformulated) {
                  throttledConsoleLog('elasticLog:400fail', 'Failed to send: ' + recordToSend.reformulated);
                  recordQueue.shift(); //drop this record
                }
                else {
                  recordToSend.reformulated = JSON.stringify(recordToSend.data);
                  recordToSend.data = {};
                }
              }
              else {
                sendDelayTicks = Math.min(maxSendDelayTicks, Math.max(0, sendDelayTicks + 500)); //ratchet send delay up to a max of 3 seconds
                recordToSend.retries = (recordToSend.retries || 0) + 1;
                if (recordQueue.length && sendDelayTicks) await sleep(sendDelayTicks);
              }
            }
            else {
              //success
              throttledConsoleLog('elasticLog:ajaxSuccess', 'yay');
              recordQueue.shift(); //move on to the next record
              sendDelayTicks = 0;
            }
          }
          else {
            sendDelayTicks = maxSendDelayTicks;
            recordToSend.retries = (recordToSend.retries || 0) + 1;
            if (recordQueue.length && sendDelayTicks) await sleep(sendDelayTicks);
          }
        }
      }
      catch (err) {
        //log and snuff (and be sure not to recursively try elastic logging here)
        asyncErr = asyncErr || { when: Date.now(), errStr: '' + err };
        const errString = 'async ElasticLog err: ' + err;
        console.log(errString);
        getCatalogErrFunc()(errString);
      }
    })();
  }
  catch (err) {
    throttledConsoleLog('elasticLog:topLevel', err.message);
  }
}

/*
 * netLog is used to wrap a network call, and elastic-logs the resulting success or failure
 * as well as the time taken in milliseconds.
 *
 * @param label is a string for logging purposes, denoting the context of the wrapped network call.
 * @param netFunc is a parameter-less function that encapsulates the network call being wrapped.
 * @return whatever the wrapped network call returns (if anything).
 */
async function netLog(label, netFunc) {
  let startTicks = Date.now();
  let result;
  const maxAttempts = 10;
  let attemptNumber;
  let errors = [];
  let mostRecentError;

  let success = false;
  for (attemptNumber = 0; attemptNumber < maxAttempts; ++attemptNumber) {
    mostRecentError = undefined;
    try {
      result = await netFunc();
      success = true;
      break;
    }
    catch (err) {
      let msg = err.message;
      msg = shortErrMsgs[msg] || msg; //make some common long msgs succinct
      errors.push(msg);
      mostRecentError = err;

      if (label == 'login') {
        //might be bad creds (which we shouldn't retry even in concept), but regardless let's
        // surface this error to the ops, they can easily retry if it's bad network.
        break;
      }
    }
    await sleep(1000);
  }

  let duration = Date.now() - startTicks;
  if (minNetTicks < duration || errors.length) {
    elasticLogNetwork({ label, success, duration, attemptNumber, errors: errors.join('|') });
  }

  if (mostRecentError) throw mostRecentError;

  return result;
}

/*
 * Gets the unique id of this launch of OnDemand.
 *
 * @return the unique id of the session.
 */
function getSession() {
  return session;
}

async function getIP(cb, refreshTicks) {
  if (ipCallUnderway) {
    console.log('getIP: retrieval already underway');
    return;
  }

  if (!cachedIpJson || cachedIpJson.error || (refreshTicks && Date.now() - cachedIpJson.when > refreshTicks)) {
    ipCallUnderway = true;
    try {
      await clearFetch(
        'https://api.ipify.org?format=json', 
        undefined, 
        {
          success: (json) => {
            cachedIpJson = { json, when: Date.now() };
          },
          bad: ({ logStr }) => {
            console.log('Error from call to ipify.org: ' + logStr);
            cachedIpJson = { error: logStr, when: Date.now() };
          },
        },
        5000
      );
    }
    finally {
      ipCallUnderway = false;
    }
  }

  if (cb) cb(cachedIpJson);

  return cachedIpJson;
}

export {
  initElasticLog,
  getSession,
  netLog,
  elasticLogConfig,
  elasticLogException,
  elasticLogTaskAction,
  elasticLogMessage,
  elasticLogEvent,
  elasticLogError,
  getIP,
};
